import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CssBaseline, ThemeProvider, createTheme, Box, CircularProgress, Typography } from '@mui/material';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import OAuthCallback from './components/OAuthCallback';
import CloudProviderSelectModal, { CloudProvider, CloudCredentials } from './components/CloudProviderSelectModal';
import TermsOfServicePage from './components/TermsOfServicePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import LandingPage from './components/LandingPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import { AzureCredentials } from './types';
import { checkGlobalCredentials, loginWithCloudForgeToken } from './services/api';
import VisualThemeSwitcher from './components/VisualThemeSwitcher';
import {
  applyPresentationMode,
  applyVisualTheme,
  getPresentationMode,
  getVisualTheme,
  normaliseTheme,
  VISUAL_THEME_KEY,
  type VisualTheme,
} from './theme/visualTheme';
import './theme/themes.css';
import { isAuthenticated, clearAuth, getStoredUser, storeAuth, cloudForgeSingleLogout } from './utils/oauth';

function loadStoredCredentials(): CloudCredentials | null {
  try {
    const raw = localStorage.getItem('cloudCredentials');
    if (raw) return JSON.parse(raw) as CloudCredentials;
  } catch {}
  return null;
}

function loadStoredProviders(): CloudProvider[] | null {
  try {
    const raw = localStorage.getItem('cloudProviders');
    if (raw) return JSON.parse(raw) as CloudProvider[];
  } catch {}
  return null;
}

/**
 * Palettes for the four visual themes CloudForge ships, converted from that
 * app's CSS token files (frontend/app/paper-theme.css, blueprint-theme.css,
 * contrast-theme.css and the base tokens in globals.css) so the two products
 * are one visual system rather than two that resemble each other.
 *
 * Accessibility corrections carried over verbatim from those files, because
 * they are the reason some of these numbers look arbitrary:
 *   Paper's primary is 44% lightness, not 53% - white-on-primary was 4.03:1,
 *   a near-miss on WCAG AA, and every primary button depends on it.
 *   Paper's divider is 64%, not 79% - card and input edges were 1.36:1 and
 *   effectively vanished.
 *   Blueprint's primary is 32% lightness for the same reason on pale stock.
 *   High Contrast is AAA by construction: 21:1 text, no grey, 2px borders.
 *
 * The --ds-* custom properties in theme/themes.css are the other half of this;
 * they cover what the design system draws directly. Change a colour in one and
 * it has to change in the other, or the screen ends up half-themed.
 */
type Ink = {
  primary: string; primaryLight: string; primaryDark: string; onPrimary: string;
  secondary: string; secondaryLight: string; secondaryDark: string;
  success: string; warning: string; error: string;
  bg: string; surface: string; surfaceAlt: string;
  text: string; textMuted: string; textHead: string;
  divider: string; cardBorder: string; borderWidth: number; inputOutline: string;
  cardShadow: string; tabIndicator: string;
};

const PALETTES: Record<VisualTheme, { light: Ink; dark: Ink }> = {
  paper: {
    light: {
      primary: '#b53e2c', primaryLight: '#d3523e', primaryDark: '#8b3123', onPrimary: '#faf9f4',
      secondary: '#da6f49', secondaryLight: '#daa95f', secondaryDark: '#ae3d2c',
      success: '#0c6b4e', warning: '#99610a', error: '#a83228',
      bg: '#efede6', surface: '#faf9f4', surfaceAlt: '#e6e3db',
      text: '#24211e', textMuted: '#756e66', textHead: '#322e2a',
      divider: '#b1a895', cardBorder: 'rgba(36,33,30,0.14)', borderWidth: 1, inputOutline: '#ada390',
      cardShadow: '0 1px 1px rgba(36,33,30,0.05), 0 12px 32px rgba(122,58,42,0.07)',
      tabIndicator: 'linear-gradient(90deg, #b53e2c, #da6f49)',
    },
    dark: {
      primary: '#ed745e', primaryLight: '#f79887', primaryDark: '#8b3123', onPrimary: '#1e1d1a',
      secondary: '#da6f49', secondaryLight: '#daa95f', secondaryDark: '#ae3d2c',
      success: '#5cbf8f', warning: '#d9a557', error: '#e8877c',
      bg: '#1e1d1a', surface: '#272520', surfaceAlt: '#34322d',
      text: '#efebe1', textMuted: '#aea89e', textHead: '#e8e3d9',
      divider: '#534e46', cardBorder: 'rgba(239,235,225,0.12)', borderWidth: 1, inputOutline: '#5e5950',
      cardShadow: '0 1px 1px rgba(0,0,0,0.15), 0 18px 46px rgba(0,0,0,0.18)',
      tabIndicator: 'linear-gradient(90deg, #ed745e, #da6f49)',
    },
  },
  classic: {
    light: {
      primary: '#e2326c', primaryLight: '#f77737', primaryDark: '#c13584', onPrimary: '#ffffff',
      secondary: '#f77737', secondaryLight: '#fca55f', secondaryDark: '#dd2a7b',
      success: '#15803d', warning: '#b45309', error: '#b91c1c',
      bg: '#ffffff', surface: '#ffffff', surfaceAlt: '#f1f5f9',
      text: '#121621', textMuted: '#636a83', textHead: '#2a2f42',
      divider: '#ebdbe3', cardBorder: 'rgba(18,22,33,0.12)', borderWidth: 1, inputOutline: '#d8c3ce',
      cardShadow: '0 1px 2px rgba(18,22,33,0.06), 0 12px 32px rgba(60,20,45,0.08)',
      tabIndicator: 'linear-gradient(90deg, #e1306c, #f77737)',
    },
    dark: {
      primary: '#e2326c', primaryLight: '#f77737', primaryDark: '#c13584', onPrimary: '#ffffff',
      secondary: '#f77737', secondaryLight: '#fca55f', secondaryDark: '#dd2a7b',
      success: '#4ade80', warning: '#fbbf24', error: '#f87171',
      bg: '#0e111b', surface: '#121621', surfaceAlt: '#1b2030',
      text: '#f8fafc', textMuted: '#94a3b8', textHead: '#e2e8f0',
      divider: '#442c3f', cardBorder: 'rgba(248,250,252,0.12)', borderWidth: 1, inputOutline: '#4a3342',
      cardShadow: '0 1px 1px rgba(0,0,0,0.2), 0 18px 46px rgba(0,0,0,0.28)',
      tabIndicator: 'linear-gradient(90deg, #e1306c, #f77737)',
    },
  },
  blueprint: {
    light: {
      primary: '#0e7490', primaryLight: '#06b6d4', primaryDark: '#0b4a5e', onPrimary: '#f4f8fd',
      secondary: '#1c3f6e', secondaryLight: '#7ba7d4', secondaryDark: '#0b2545',
      success: '#0f6b4f', warning: '#8a5a08', error: '#a3312a',
      bg: '#e8f0fa', surface: '#f4f8fd', surfaceAlt: '#dce9f6',
      // 42% lightness, not the source file's 47%: #5a7699 measured 4.07:1 on
      // Blueprint's pale stock, under AA for body copy. This is 4.99:1.
      text: '#0b2545', textMuted: '#4f6887', textHead: '#1c3f6e',
      divider: '#b9d0e8', cardBorder: 'rgba(11,37,69,0.18)', borderWidth: 1, inputOutline: '#7ba7d4',
      cardShadow: '0 1px 1px rgba(11,37,69,0.06), 0 16px 40px rgba(11,37,69,0.08)',
      tabIndicator: 'linear-gradient(90deg, #0e7490, #06b6d4)',
    },
    dark: {
      primary: '#38bdf8', primaryLight: '#7dd3fc', primaryDark: '#0e7490', onPrimary: '#071726',
      secondary: '#7dd3fc', secondaryLight: '#bae6fd', secondaryDark: '#0e7490',
      success: '#4ade9f', warning: '#e0b055', error: '#f19a91',
      bg: '#071726', surface: '#0d2137', surfaceAlt: '#123049',
      text: '#dbeafe', textMuted: '#7d9dc4', textHead: '#bfdbfe',
      divider: '#1e4468', cardBorder: 'rgba(219,234,254,0.14)', borderWidth: 1, inputOutline: '#2f6390',
      cardShadow: '0 1px 1px rgba(0,0,0,0.22), 0 18px 46px rgba(0,0,0,0.34)',
      tabIndicator: 'linear-gradient(90deg, #38bdf8, #7dd3fc)',
    },
  },
  contrast: {
    // AAA: pure black on pure white, no grey text at all, 2px borders and no
    // shadows - a shadow conveys nothing to the reader this theme exists for.
    light: {
      primary: '#0b4fa8', primaryLight: '#0b4fa8', primaryDark: '#00306b', onPrimary: '#ffffff',
      secondary: '#0b4fa8', secondaryLight: '#0b4fa8', secondaryDark: '#00306b',
      success: '#005c2e', warning: '#6b4200', error: '#a1001a',
      bg: '#ffffff', surface: '#ffffff', surfaceAlt: '#ebebeb',
      text: '#000000', textMuted: '#000000', textHead: '#000000',
      divider: '#000000', cardBorder: '#000000', borderWidth: 2, inputOutline: '#000000',
      cardShadow: 'none',
      tabIndicator: '#0b4fa8',
    },
    dark: {
      primary: '#8ab4f8', primaryLight: '#b3cdfb', primaryDark: '#8ab4f8', onPrimary: '#000000',
      secondary: '#8ab4f8', secondaryLight: '#b3cdfb', secondaryDark: '#8ab4f8',
      success: '#6ee7a8', warning: '#ffce6b', error: '#ff9a9a',
      bg: '#000000', surface: '#000000', surfaceAlt: '#1f1f1f',
      text: '#ffffff', textMuted: '#ffffff', textHead: '#ffffff',
      divider: '#ffffff', cardBorder: '#ffffff', borderWidth: 2, inputOutline: '#ffffff',
      cardShadow: 'none',
      tabIndicator: '#8ab4f8',
    },
  },
};

// Paper sets headings in a serif; Classic stays in the UI face it always used;
// Blueprint annotates in monospace, the way a real drawing is labelled; High
// Contrast keeps the UI face because a display face is one more thing between
// the reader and the words.
const HEADING_FONT: Record<VisualTheme, string> = {
  paper: 'Georgia, "Times New Roman", serif',
  classic: 'Inter, ui-sans-serif, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  blueprint: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  contrast: 'Inter, ui-sans-serif, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
};

// Corner radius is part of each theme's voice: Paper's tight printed edge,
// Classic's softer 8px, Blueprint's square drafting corner, and High Contrast's
// near-square, where a large radius eats into an already-thick border.
const RADIUS: Record<VisualTheme, number> = { paper: 5, classic: 8, blueprint: 2, contrast: 3 };

const getTheme = (mode: 'light' | 'dark' = 'light', visual: VisualTheme = 'paper') => {
  const c = PALETTES[visual][mode];
  const radius = RADIUS[visual];

  return createTheme({
    palette: {
      mode,
      primary: { main: c.primary, light: c.primaryLight, dark: c.primaryDark, contrastText: c.onPrimary },
      secondary: { main: c.secondary, light: c.secondaryLight, dark: c.secondaryDark },
      // Semantic colours stay semantic - green must read as "healthy" whatever
      // the brand palette is - but each theme tunes them to its own ground.
      success: { main: c.success },
      warning: { main: c.warning },
      error: { main: c.error },
      background: { default: c.bg, paper: c.surface },
      text: { primary: c.text, secondary: c.textMuted },
      divider: c.divider,
    },
    typography: {
      fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      h4: { fontFamily: HEADING_FONT[visual], fontWeight: 700 },
      h5: { fontFamily: HEADING_FONT[visual], fontWeight: 700 },
      h6: { fontWeight: 700 },
      button: { letterSpacing: 0, textTransform: 'none' },
    },
    shape: { borderRadius: radius },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // Flat ground. Blueprint paints its drafting grid over this from
            // theme/themes.css, which is why this is a colour and not a gradient.
            background: c.bg,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius + 3,
            border: `${c.borderWidth}px solid ${c.cardBorder}`,
            // None of these themes is glassmorphic - no backdrop blur, no image.
            backgroundImage: 'none',
            backgroundColor: c.surface,
            boxShadow: c.cardShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', borderColor: c.cardBorder },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 999, fontWeight: 700 } },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: radius, textTransform: 'none', fontWeight: 700, boxShadow: 'none' },
          containedPrimary: {
            // Solid rather than a gradient: these are flat surfaces, and it is
            // the primary's own contrast ratio that carries AA on the button.
            background: c.primary,
            '&:hover': { background: c.primaryDark, boxShadow: c.cardShadow },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: radius, backgroundColor: c.surface },
          notchedOutline: { borderColor: c.inputOutline, borderWidth: c.borderWidth },
        },
      },
      MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: c.cardBorder },
          head: {
            color: c.textHead,
            backgroundColor: c.surfaceAlt,
            fontWeight: 800,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { borderRadius: radius + 3, border: `${c.borderWidth}px solid ${c.cardBorder}` },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: 3, background: c.tabIndicator },
        },
      },
    },
  });
};

// ── View states ───────────────────────────────────────────────────────────────
type View = 'loading' | 'cf-handoff' | 'landing' | 'sso-login' | 'forgot-password' | 'oauth-callback' | 'cloud-select' | 'dashboard' | 'terms' | 'privacy';

function isOAuthCallbackUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
}

// ── CloudForge SSO handoff (?cf_token=<jwt>&embedded=true) ────────────────────
// CloudForge's launcher opens /lens?cf_token=… — the query string survives the
// /lens base path, so plain location.search works here.
function getCloudForgeHandoff(): { token: string; embedded: boolean } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('cf_token');
  if (!token) return null;
  return { token, embedded: params.get('embedded') === 'true' };
}

function stripCloudForgeParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('cf_token');
  url.searchParams.delete('embedded');
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  const [view, setView] = useState<View>('loading');
  const [prevView, setPrevView] = useState<View>('landing');
  const [loginInitialTab, setLoginInitialTab] = useState<'signin' | 'signup'>('signin');
  const [credentials, setCredentials] = useState<AzureCredentials | null>(null);
  const [selectedProviders, setSelectedProviders] = useState<CloudProvider[]>(loadStoredProviders() ?? []);
  const [cloudCredentials, setCloudCredentials] = useState<CloudCredentials>(loadStoredCredentials() ?? {});
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [cfHandoffError, setCfHandoffError] = useState<string | undefined>(undefined);

  // Read on mount rather than during render: the value lives in localStorage,
  // which is not there until the browser is.
  const [visualTheme, setVisualTheme] = useState<VisualTheme>('paper');

  useEffect(() => {
    const saved = getVisualTheme();
    setVisualTheme(saved);
    applyVisualTheme(saved);
    applyPresentationMode(getPresentationMode());

    // CloudLens and CloudForge are the same origin (cloudforge.cloud and
    // cloudforge.cloud/lens), so they share this localStorage key: a theme
    // changed in a CloudForge tab arrives here as a storage event and should
    // take effect without a reload.
    const syncAcrossTabs = (event: StorageEvent) => {
      if (event.key !== VISUAL_THEME_KEY) return;
      const next = normaliseTheme(event.newValue);
      setVisualTheme(next);
      applyVisualTheme(next);
    };

    window.addEventListener('storage', syncAcrossTabs);
    return () => window.removeEventListener('storage', syncAcrossTabs);
  }, []);

  const theme = useMemo(() => getTheme('light', visualTheme), [visualTheme]);

  // Detect special entry points before anything else
  useEffect(() => {
    const handoff = getCloudForgeHandoff();
    if (handoff) {
      handleCloudForgeHandoff(handoff);
      return;
    }
    if (isOAuthCallbackUrl()) {
      setView('oauth-callback');
      return;
    }
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrap = useCallback(async () => {
    // 1. User must have a valid JWT
    if (!isAuthenticated()) {
      setView('landing');
      return;
    }

    // 2. Load global Azure credentials
    try {
      const result = await checkGlobalCredentials();
      if (result.exists && result.subscriptions) {
        const creds: AzureCredentials = {
          sessionId: 'global-' + Date.now(),
          subscriptionIds: result.subscriptions.map((s: any) => s.subscriptionId),
          subscriptions: result.subscriptions,
          tenantId: '',
          clientId: '',
          clientSecret: '',
        };
        setCredentials(creds);
        setCredentials(creds);
      } else {
        // Authenticated but no Azure credentials — admin needs to configure them
        setCredentials({
          sessionId: 'global-pending',
          subscriptionIds: [],
          subscriptions: [],
          tenantId: '',
          clientId: '',
          clientSecret: '',
        });
      }
    } catch {
      // ignore
    }

    // Show provider selection modal if no stored choice yet
    const stored = loadStoredProviders();
    if (!stored || stored.length === 0) {
      setView('cloud-select');
    } else {
      setView('dashboard');
    }
  }, []);

  // Exchange a CloudForge handoff token for a CloudLens session (SSO from
  // cloudforge.cloud → /lens). Success stores auth exactly like the normal
  // login flow (storeAuth) and continues into bootstrap; failure falls through
  // to the regular login page with an inline error.
  const handleCloudForgeHandoff = useCallback(async ({ token, embedded }: { token: string; embedded: boolean }) => {
    setView('cf-handoff');

    if (embedded) {
      try { sessionStorage.setItem('cloudlens_embedded', 'true'); } catch { /* ignore */ }
    }

    try {
      const result = await loginWithCloudForgeToken(token);
      if (!result.success || !result.token || !result.user) {
        throw new Error(result.error ?? 'CloudForge sign-in failed');
      }
      storeAuth(result.token, result.user);
      stripCloudForgeParams();
      setView('loading');
      bootstrap();
    } catch {
      stripCloudForgeParams();
      setCfHandoffError('CloudForge sign-in failed — log in below.');
      setLoginInitialTab('signin');
      setView('sso-login');
    }
  }, [bootstrap]);

  // After OAuth callback completes successfully, re-run bootstrap
  const handleAuthSuccess = useCallback(() => {
    setView('loading');
    bootstrap();
  }, [bootstrap]);

  const handleAuthError = useCallback(() => {
    clearAuth();
    setView('landing');
  }, []);

  const handleDisconnect = useCallback(() => {
    clearAuth();
    setCredentials(null);
    // Single logout: when this session came from the CloudForge handoff
    // (same-origin at cloudforge.cloud/lens), also sign out of CloudForge and
    // land on its login page. Standalone sessions fall through unchanged.
    if (cloudForgeSingleLogout()) return;
    setView('landing');
  }, []);

  const handleProviderConfirm = useCallback((providers: CloudProvider[], creds: CloudCredentials) => {
    localStorage.setItem('cloudProviders', JSON.stringify(providers));
    localStorage.setItem('cloudCredentials', JSON.stringify(creds));
    setSelectedProviders(providers);
    setCloudCredentials(creds);
    setProviderModalOpen(false);
    setView('dashboard');
  }, []);

  const handleOpenProviderModal = useCallback(() => {
    setProviderModalOpen(true);
  }, []);

  const handleCloseProviderModal = useCallback(() => {
    setProviderModalOpen(false);
  }, []);

  const handleCloseInitialProviderModal = useCallback(() => {
    setView('dashboard');
  }, []);

  const handleGoToSignIn = useCallback(() => {
    setCfHandoffError(undefined);
    setLoginInitialTab('signin');
    setView('sso-login');
  }, []);

  const handleForgotPassword = useCallback(() => {
    setView('forgot-password');
  }, []);

  const handleGetStarted = useCallback(() => {
    setCfHandoffError(undefined);
    setLoginInitialTab('signup');
    setView('sso-login');
  }, []);

  const handleOpenTerms = useCallback(() => {
    setPrevView(view);
    setView('terms');
  }, [view]);

  const handleOpenPrivacy = useCallback(() => {
    setPrevView(view);
    setView('privacy');
  }, [view]);

  const handleBackFromLegal = useCallback(() => {
    setView(prevView);
  }, [prevView]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <VisualThemeSwitcher value={visualTheme} onChange={setVisualTheme} />

      {view === 'loading' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
          background: '#efede6',
        }}>
          <Box sx={{
            p: 4,
            minWidth: 300,
            textAlign: 'center',
            borderRadius: 5,
            bgcolor: '#faf9f4',
            border: '1px solid rgba(36,33,30,0.14)',
            boxShadow: '0 1px 1px rgba(36,33,30,0.05), 0 18px 46px rgba(122,58,42,0.10)',
          }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="h6" color="text.primary" sx={{ mt: 2, fontWeight: 800 }}>
              Loading CloudLens…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Preparing your cloud command center
            </Typography>
          </Box>
        </Box>
      )}

      {view === 'cf-handoff' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
          background: '#efede6',
        }}>
          <Box sx={{
            p: 4,
            minWidth: 300,
            textAlign: 'center',
            borderRadius: 5,
            bgcolor: '#faf9f4',
            border: '1px solid rgba(36,33,30,0.14)',
            boxShadow: '0 1px 1px rgba(36,33,30,0.05), 0 18px 46px rgba(122,58,42,0.10)',
          }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="h6" color="text.primary" sx={{ mt: 2, fontWeight: 800 }}>
              Signing you in from CloudForge…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Completing single sign-on
            </Typography>
          </Box>
        </Box>
      )}

      {view === 'landing' && (
        <LandingPage
          onSignIn={handleGoToSignIn}
          onGetStarted={handleGetStarted}
          onOpenTerms={handleOpenTerms}
          onOpenPrivacy={handleOpenPrivacy}
        />
      )}

      {view === 'sso-login' && (
        <LoginPage
          initialTab={loginInitialTab}
          initialError={cfHandoffError}
          onOpenTerms={handleOpenTerms}
          onOpenPrivacy={handleOpenPrivacy}
          onForgotPassword={handleForgotPassword}
        />
      )}

      {view === 'forgot-password' && (
        <ForgotPasswordPage onBack={handleGoToSignIn} />
      )}

      {view === 'terms' && <TermsOfServicePage onBack={handleBackFromLegal} />}
      {view === 'privacy' && <PrivacyPolicyPage onBack={handleBackFromLegal} />}

      {view === 'oauth-callback' && (
        <OAuthCallback onSuccess={handleAuthSuccess} onError={handleAuthError} />
      )}

      {/* Cloud provider selection — shown after first login or when user reopens it */}
      {view === 'cloud-select' && (
        <CloudProviderSelectModal
          open={true}
          initialProviders={selectedProviders.length > 0 ? selectedProviders : undefined}
          initialCredentials={cloudCredentials}
          onConfirm={handleProviderConfirm}
          onClose={handleCloseInitialProviderModal}
        />
      )}

      {view === 'dashboard' && (
        <>
          <Dashboard
            credentials={credentials!}
            onDisconnect={handleDisconnect}
            currentUser={getStoredUser()}
            selectedProviders={selectedProviders}
            onChangeProviders={handleOpenProviderModal}
            cloudCredentials={cloudCredentials}
          />
          {/* Provider re-select modal (from navbar/settings) */}
          <CloudProviderSelectModal
            open={providerModalOpen}
            initialProviders={selectedProviders}
            initialCredentials={cloudCredentials}
            onConfirm={handleProviderConfirm}
            onClose={handleCloseProviderModal}
          />
        </>
      )}
    </ThemeProvider>
  );
}

export default App;
